import {
	Paper,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Zoom,
} from '@mui/material';
import { Avatar, Checkbox, Table } from 'flowbite-react';
import React, { useState } from 'react';
import { displayApplicationStatus, showApplicationStatusToRecruiterCard, stringAvatar } from 'shared-utils';
import { ApplicationRoundStatus, UIUserRecommendation } from 'types';
import { LightTooltip } from './RecruiterCandidateCardHeader';
import VerifiedTooltip from '../profile/VerifiedTooltip';
import { useThemeContext } from 'src/context/ThemeProvider';
import MatchPercentageCircularBarComponent from './MatchPercentageCircularBarComponent';
import { generateExpectedSalaryString } from 'src/utils/profileAdditionalInfo';
import RecruiterCandidateCardFooter from './RecruiterCandidateCardFooter';
import CurrentRoundProgress from './CurrentRoundProgress';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from 'src/context/UserProvider';

type candidateTableColumnHeaderType = {
	id: string;
	label: string;
	align: 'left' | 'center' | 'right' | 'justify' | 'inherit' | undefined;
	minWidth: string;
};

const RecruiterPipelineTable = ({
	positionId,
	bulkSelect,
	candidates,
	disableStartProcess,
	bulkSelectedCandidates,
	setBulkSelectedCandidates,
	isPositionExpired = false,
}: {
	positionId: string;
	bulkSelect: boolean;
	isPositionExpired?: boolean;
	disableStartProcess: boolean;
	candidates: UIUserRecommendation[];
	bulkSelectedCandidates: UIUserRecommendation[];
	setBulkSelectedCandidates: React.Dispatch<React.SetStateAction<UIUserRecommendation[]>>;
}) => {
	const navigate = useNavigate();
	const { companyId } = useParams();
	const [page, setPage] = useState(0);
	const { themeMode } = useThemeContext();
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const { isAccountManagerLoggedIn } = useUserContext();

	const tableColumns: candidateTableColumnHeaderType[] = [
		{ id: 'candidate', label: 'Candidate', align: 'left', minWidth: 'fit-content' },
		{ id: 'match', label: 'Match %', align: 'center', minWidth: 'fit-content' },
		{ id: 'phone', label: 'Phone', align: 'left', minWidth: 'fit-content' },
		// { id: 'salary', label: 'Predicted Salary', align: 'left', minWidth: 'fit-content' },
		{ id: 'status', label: 'Round Status', align: 'left', minWidth: 'fit-content' },
		{ id: 'action', label: 'Actions', align: 'right', minWidth: 'fit-content' },
	];

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setBulkSelectedCandidates(candidates);
			return;
		}
		setBulkSelectedCandidates([]);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const tableRowContent = (tableRow: string, candidateData: UIUserRecommendation) => {
		const maxLength = 15;
		switch (tableRow.toLowerCase()) {
			case 'candidate': {
				const role = candidateData.user.profile.currentRole ?? '';
				const company = candidateData.user.profile.currentCompany ?? '';
				const companyString = role && company ? `${role} @ ${company}` : undefined;
				const yearsOfExperience = candidateData.user.profile.totalExperience;
				const experienceString =
					yearsOfExperience !== undefined
						? yearsOfExperience
							? `Exp ${
									yearsOfExperience > 1
										? `${Math.floor(yearsOfExperience)} yrs ${Math.ceil(yearsOfExperience * 12) % 12} mon`
										: `${Math.floor(yearsOfExperience)} yr ${Math.ceil(yearsOfExperience * 12) % 12} mon`
							  }`
							: 'Fresher'
						: undefined;
				const subTextCandidate =
					companyString && experienceString
						? `${companyString} | ${experienceString}`
						: companyString ?? experienceString;
				return (
					<div className="flex grow-2 gap-2">
						<Avatar
							size="md"
							img={candidateData?.user?.profile?.profileImageUrl || candidateData?.user?.profile?.imageUrl}
							rounded
							placeholderInitials={stringAvatar(candidateData?.user?.name.trim().toUpperCase() || '')}
						/>

						<div>
							<div className="text-[18px] font-[600] text-[#1C1C28] dark:text-white">
								<div className="flex gap-1">
									{candidateData?.user?.name.length > maxLength ? (
										<>
											<Tooltip title={candidateData?.user?.name} placement="top">
												<span
													onClick={e => {
														e.stopPropagation();
													}}
												>
													{candidateData?.user?.name?.substring(0, maxLength)}...
												</span>
											</Tooltip>
										</>
									) : (
										<>{candidateData?.user?.name}</>
									)}
									{candidateData?.user?.flags.isTrusted ? (
										<div className="flex flex-col justify-center">
											<LightTooltip
												light={!(themeMode === 'dark')}
												title={<VerifiedTooltip />}
												placement="bottom"
												arrow={false}
											>
												<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
											</LightTooltip>
										</div>
									) : null}
								</div>
							</div>
							<div className="text-[12px] font-[500] text-[#28293D] dark:text-[#C7C9D9]">
								{subTextCandidate && subTextCandidate.length > maxLength ? (
									<>
										<Tooltip title={subTextCandidate}>
											<span
												onClick={e => {
													e.stopPropagation();
												}}
											>
												{subTextCandidate.substring(0, maxLength)}...
											</span>
										</Tooltip>
									</>
								) : (
									<>{subTextCandidate}</>
								)}
							</div>
						</div>
					</div>
				);
			}
			case 'match': {
				return (
					<div className="w-[39.692px] mx-auto">
						<MatchPercentageCircularBarComponent value={Math.round(candidateData?.percentageMatch ?? 0)} />
					</div>
				);
			}
			case 'phone': {
				return <>{candidateData?.user?.phone ? candidateData?.user?.phone : '-'}</>;
			}
			case 'salary': {
				return (
					<>
						{candidateData.user.evaluation.predictedSalary?.min || candidateData.user.evaluation.predictedSalary?.max
							? generateExpectedSalaryString(
									candidateData.user.evaluation.predictedSalary?.min,
									candidateData.user.evaluation.predictedSalary?.min ===
										candidateData.user.evaluation.predictedSalary?.max
										? 0
										: candidateData.user.evaluation.predictedSalary?.max,
							  )
							: '-'}
					</>
				);
			}
			case 'status': {
				return (
					<CurrentRoundProgress
						showStatus={
							candidateData.application?.ongoingRound?.status === ApplicationRoundStatus.RequestedForReschedule
								? 'Requested for Reschedule'
								: candidateData.application?.status &&
								    showApplicationStatusToRecruiterCard(candidateData.application?.status)
								  ? displayApplicationStatus(candidateData.application.status)
								  : undefined
						}
						currentRoundNumber={
							candidateData?.application?.ongoingRound?.order
								? candidateData?.application?.ongoingRound?.order
								: candidateData?.application?.applicationRounds?.length
								  ? Number(candidateData.application.applicationRounds?.length) + 1
								  : 0
						}
						totalRounds={candidateData?.application?.applicationRounds?.length || 0}
					/>
				);
			}
			case 'action': {
				return (
					<RecruiterCandidateCardFooter
						showRoundProgress={false}
						userId={candidateData.user?._id}
						data={candidateData}
						isShortListed={true}
						showShortListUi={false}
						isPositionExpired={isPositionExpired}
						level={Math.round(candidateData.user.evaluation.level ?? 0)}
						disableStartProcess={disableStartProcess || candidateData?.application?.pendingForAdminApproval}
						disableViewProfile={disableStartProcess}
						hasShownDisInterest={false}
						hasShownInterest={true}
						handleViewProfile={() => {}}
						handler={candidateData.user.handler}
						containerProps={{ className: '' }}
					/>
				);
			}
		}
	};

	return (
		<Paper
			sx={{
				mt: '24px',
				bg: 'white',
				width: '100%',
				overflow: 'hidden',
				borderRadius: '8px',
				boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
				isolation: 'isolate',
			}}
		>
			<TableContainer sx={{ maxHeight: 440 }}>
				<Table aria-label="sticky table">
					<TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
						<TableRow
							sx={{
								'&': {
									background: '#F2F2F5',
									'[data-mode="dark"] &': {
										background: '#18222D',
									},
								},
							}}
						>
							<Zoom in={bulkSelect} mountOnEnter unmountOnExit>
								<Zoom in={bulkSelect} mountOnEnter unmountOnExit>
									<TableCell align="center" padding="checkbox">
										<Checkbox color="purplePlaced" onChange={handleSelectAllClick} />
									</TableCell>
								</Zoom>
							</Zoom>
							{tableColumns.map((column, id) => (
								<TableCell
									className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
									key={id}
									align={column.align}
									style={{ minWidth: column.minWidth }}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody
						sx={{
							'&': {
								'[data-mode="dark"] &': {
									background: '#18222D',
								},
							},
						}}
					>
						{candidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate, id) => {
							const isSelected = bulkSelectedCandidates.find(
								bulkCandidate => bulkCandidate.user.handler === candidate.user.handler,
							)
								? true
								: false;

							return (
								<TableRow
									aria-checked={isSelected}
									hover
									role="checkbox"
									tabIndex={-1}
									key={candidate._id}
									onClick={() => {
										if (!bulkSelect) {
											console.log(
												'/positions',
												`/positions/${companyId}/${positionId}/candidates?handler=${candidate?.user?.handler}`,
											);
											navigate(
												isAccountManagerLoggedIn
													? `/positions/${companyId}/${positionId}/candidates?handler=${candidate?.user?.handler}&tab=connected`
													: `/positions/${positionId}/candidates?handler=${candidate?.user?.handler}&tab=connected`,
											);
											return;
										}
										if (isSelected)
											setBulkSelectedCandidates(prev =>
												prev.filter(people => people?.user?.handler !== candidate.user.handler),
											);
										else setBulkSelectedCandidates(prev => [...prev, candidate]);
									}}
								>
									<Zoom in={bulkSelect} mountOnEnter unmountOnExit>
										<TableCell align="center" padding="checkbox">
											<Checkbox color="purplePlaced" checked={isSelected} />
										</TableCell>
									</Zoom>
									{tableColumns.map((column, id) => {
										return (
											<TableCell key={candidate._id + id} align={column.align}>
												{tableRowContent(column.id, candidate)}
											</TableCell>
										);
									})}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				sx={{
					'&': {
						background: '#FAFAFC',
						'[data-mode="dark"] &': {
							background: '#18222D',
						},
					},
				}}
				rowsPerPageOptions={[10, 25, 100]}
				component="div"
				count={candidates.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Paper>
	);
};

export default RecruiterPipelineTable;
